import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { indexStore } from "src/store/indexStore";
import { formatDate, getFirstDayOfMonth, getToday } from "../utils/FormatDate";
import { Combobox } from "../selectbox/ComboBox";
import { MainDialog } from "../Dialgos/MainDialog";
import LogDetailes from "./LogDetailes";
import Iterator from "../utils/Iterator";
import { Button } from "../ui/button";
import { DatePickerWithRange } from "../DatePicker/DatePickerRange";
import { Loader2, Activity, Search, Calendar, User, FileText, Sparkles } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import Visibility from "../utils/Visibility";
function Logs() {
  const { t } = useTranslation();
  const {
    logs,
    fetchUsersFunction,
    users,
    fetchLogsByUserFunction,
    logsLoading,
  } = indexStore();
  useEffect(() => {
    if(users.length === 0) fetchUsersFunction();
  }, [users.length, fetchUsersFunction]);
  const [userSelected, setUserSelected] = React.useState(null);
  const [dateSelected, setDateSelected] = React.useState({
    from: getFirstDayOfMonth(),
    to: getToday(),
  });

  const submitParamsLogs = () => {
    fetchLogsByUserFunction(userSelected, {
      startDate: dateSelected.from,
      endDate: dateSelected.to,
    });
  };

  const hasFilters = userSelected && dateSelected.from && dateSelected.to;

  return (
    <div className="flex flex-col gap-6">
      {/* Header Section */}
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-lg bg-gradient-to-br from-primary/10 to-transparent border">
          <Activity className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-foreground">{t("dashbord.user_activity")}</h2>
          <p className="text-sm text-muted-foreground">
            {t("gloabl.manage_activity_logs") || "View and track user activity logs"}
          </p>
        </div>
      </div>

      {/* Filters Card */}
      <Card className="border-2">
        <CardHeader className="pb-3 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-muted">
              <Search className="w-5 h-5 text-muted-foreground" />
            </div>
            <div>
              <CardTitle className="text-lg">{t("gloabl.filter_logs") || "Filter Logs"}</CardTitle>
              <CardDescription>{t("gloabl.filter_logs_desc") || "Select user and date range to view activity logs"}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <User className="w-4 h-4" />
                {t("gloabl.user")}
              </label>
              <Combobox
                data={users}
                onChange={(e) => setUserSelected(e)}
                value={userSelected}
                placeholder={t("gloabl.select") + " " + t("gloabl.user")}
                lable={"name"}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {t("gloabl.date_range") || "Date Range"}
              </label>
              <DatePickerWithRange setDate={setDateSelected} date={dateSelected} />
            </div>

            <div className="flex items-end">
              <Button
                disabled={logsLoading || !hasFilters}
                onClick={submitParamsLogs}
                className="w-full"
                size="lg"
              >
                {logsLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t("gloabl.processing")}
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    {t("gloabl.search")}
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <div className="flex-1">
        <Visibility visible={logsLoading}>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            </CardContent>
          </Card>
        </Visibility>

        <Visibility visible={!logsLoading && logs.length === 0 && hasFilters}>
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileText className="w-16 h-16 mb-4 text-gray-300" />
              <p className="text-xl font-semibold mb-2">{t("gloabl.no_logs_found") || "No logs found"}</p>
              <p className="text-sm text-muted-foreground text-center">
                {t("gloabl.no_logs_description") || "No activity logs found for the selected user and date range"}
              </p>
            </CardContent>
          </Card>
        </Visibility>

        <Visibility visible={!logsLoading && logs.length === 0 && !hasFilters}>
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Activity className="w-16 h-16 mb-4 text-gray-300" />
              <p className="text-xl font-semibold mb-2">{t("gloabl.select_filters") || "Select Filters"}</p>
              <p className="text-sm text-muted-foreground text-center">
                {t("gloabl.select_user_and_date")}
              </p>
            </CardContent>
          </Card>
        </Visibility>

        <Visibility visible={!logsLoading && logs.length > 0}>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-semibold">
                  {t("gloabl.logs_found") || "Activity Logs"} ({logs.length})
                </h3>
              </div>
            </div>
            <Card className="border-2">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-semibold">{t("gloabl.item")}</TableHead>
                      <TableHead className="font-semibold">{t("gloabl.action")}</TableHead>
                      <TableHead className="font-semibold">{t("gloabl.model")}</TableHead>
                      <TableHead className="font-semibold">{t("gloabl.user")}</TableHead>
                      <TableHead className="font-semibold">{t("gloabl.date")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <Iterator
                      data={logs}
                      render={(log) => {
                        // Get display name based on model
                        const getDisplayName = () => {
                          if (log.details?.name_en) return log.details.name_en;
                          if (log.details?.name_ar) return log.details.name_ar;
                          if (log.details?.code) return log.details.code;
                          if (log.details?.order_number) return `#${log.details.order_number}`;
                          if (log.details?.table_number) return `${t("gloabl.table")} ${log.details.table_number}`;
                          if (log.details?.phone) return log.details.phone;
                          return `${t("gloabl.model")} ID: ${log.model_id}`;
                        };

                        const getActionColor = (action) => {
                          switch (action) {
                            case "Add":
                              return "bg-green-100 text-green-800 border-green-200 dark:bg-green-900 dark:text-green-200";
                            case "Update":
                              return "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900 dark:text-blue-200";
                            case "Delete":
                              return "bg-red-100 text-red-800 border-red-200 dark:bg-red-900 dark:text-red-200";
                            default:
                              return "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-200";
                          }
                        };

                        return (
                          <MainDialog
                            key={log.id}
                            title={t("gloabl.details")}
                            trigger={
                              <TableRow className="cursor-pointer hover:bg-muted/50 transition-colors" key={log.id}>
                                <TableCell className="font-medium">
                                  {getDisplayName()}
                                </TableCell>
                                <TableCell>
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getActionColor(log.action)}`}>
                                    {t("actions." + log.action)}
                                  </span>
                                </TableCell>
                                <TableCell>
                                  <span className="text-sm text-muted-foreground">
                                    {t("models." + log.model)}
                                  </span>
                                </TableCell>
                                <TableCell className="text-sm">
                                  {log.user || t("gloabl.no_data")}
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                  {formatDate(new Date(log.created_at))}
                                </TableCell>
                              </TableRow>
                            }
                          >
                            <LogDetailes log={log} />
                          </MainDialog>
                        );
                      }}
                    />
                  </TableBody>
                </Table>
              </div>
            </Card>
          </div>
        </Visibility>
      </div>
    </div>
  );
}

export default Logs;
